import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useUser, type User } from './useData';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Button } from '../../components/ui/Button';
import { Switch } from '../../components/ui/Switch';
import { Accordion, AccordionItem } from '../../components/ui/Accordion';
import { ChevronLeftIcon } from '../../components/ui/Icons';
import { cn } from '../../lib/utils';

interface PermissionRow {
  moduleKey: string;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

interface PermissionsResponse {
  permissions: PermissionRow[];
  hasCustomRestrictions: boolean;
}

/** Labels for permission modules (match sidebar item names). */
const MODULE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  reminders: 'Reminders',
  clients: 'Clients',
  leads: 'Leads',
  offers: 'Offers',
  catalog_items: 'Catalog',
  offer_templates: 'Offer Templates',
  calls: 'Calls',
  appointments: 'Appointments',
  crm_notes: 'Notes',
  marketing: 'Marketing',
  crm_tasks: 'Tasks',
  todos: 'Todos',
  projects: 'Projects',
  support_ticket: 'Support Tickets',
  knowledge_base: 'Knowledge Base',
  client_services: 'Services',
  packages: 'Packages',
  support_agreements: 'Support Agreements',
  client_vault: 'Client Vault',
  vault_templates: 'Templates',
  client_invoices: 'Invoices',
  invoice_payments: 'Payments',
  supplier_invoices: 'Supplier Invoices',
  suppliers: 'Suppliers',
  supplier_payments: 'Supplier Payments',
  payables: 'Payables List',
  payees: 'Payees',
  payees_projection: 'Payees Projection',
  recurring_payables: 'Recurring Payables',
  report_generator: 'Report Generator',
  reports: 'Reports',
  users: 'Users',
  module_management: 'Module Management',
  settings: 'Application Settings',
  deleted_files: 'File Storage',
  notifications: 'Notifications',
  configuration_settings: 'Configuration & Settings',
  presets_management: 'Presets',
  custom_fields: 'Custom Fields',
  workflows: 'Workflows',
  import_invoice: 'Import Invoice',
  import_gemi: 'GEMI Preview',
  import_11888: '11888 Preview',
  database_browser: 'Database Browser',
  activity_logs: 'Activity Logs',
  // Legacy/params (not in sidebar; kept for API)
  expenses: 'Expenses',
  expense_categories: 'Expense Categories',
  expense_subcategories: 'Expense Subcategories',
  task_type: 'Task Type',
  task_category: 'Task Category',
  task_status: 'Task Status',
  priority: 'Priority',
  ticket_status: 'Ticket Status',
  call_type: 'Call Type',
  service_type: 'Service Type',
  frequency_type: 'Frequency Type',
  duration: 'Duration',
  service_category: 'Service Category',
  reminder_type: 'Reminder Type',
  tax_authority: 'Tax Authority',
  p_country: 'Countries',
  app_status: 'App Status',
  invoice_category: 'Invoice Category',
  invoice_type: 'Invoice Type',
  manufacturer: 'Manufacturer',
  catalog_category: 'Catalog Category',
  tags: 'Tags',
};

/** Groups and items exactly as in sidebar (same order, same names). */
const PERMISSION_GROUPS: { title: string; keys: string[] }[] = [
  { title: 'Dashboard', keys: ['dashboard'] },
  { title: 'Communication', keys: ['reminders'] },
  { title: 'CRM', keys: ['clients', 'leads', 'offers', 'catalog_items', 'offer_templates', 'calls', 'appointments', 'crm_notes', 'marketing'] },
  { title: 'Work Management', keys: ['crm_tasks', 'todos', 'projects'] },
  { title: 'Support', keys: ['support_ticket', 'knowledge_base'] },
  { title: 'Services & Delivery', keys: ['client_services', 'packages', 'support_agreements', 'client_vault', 'vault_templates'] },
  { title: 'Finance', keys: ['client_invoices', 'invoice_payments', 'supplier_invoices', 'suppliers', 'supplier_payments'] },
  { title: 'Payables', keys: ['payables', 'payees', 'payees_projection', 'recurring_payables'] },
  { title: 'Reports', keys: ['report_generator', 'reports'] },
  { title: 'Settings – System', keys: ['users', 'module_management', 'settings', 'deleted_files', 'notifications', 'configuration_settings'] },
  { title: 'Settings – Customization', keys: ['presets_management', 'custom_fields', 'workflows'] },
  { title: 'Settings – Tools', keys: ['import_invoice', 'import_gemi', 'import_11888', 'database_browser', 'activity_logs'] },
];

function normalizePermissionsPayload(data: unknown): { permissions: PermissionRow[]; hasCustomRestrictions: boolean } {
  if (Array.isArray(data)) {
    return { permissions: data as PermissionRow[], hasCustomRestrictions: true };
  }
  const obj = data as PermissionsResponse | null;
  if (obj && Array.isArray(obj.permissions)) {
    return {
      permissions: obj.permissions,
      hasCustomRestrictions: Boolean(obj.hasCustomRestrictions),
    };
  }
  return { permissions: [], hasCustomRestrictions: false };
}

const UserPermissionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const userId = id ? parseInt(id, 10) : NaN;
  const { user, isLoading: userLoading } = useUser(userId);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [hasCustomRestrictions, setHasCustomRestrictions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [otherUsers, setOtherUsers] = useState<User[]>([]);
  const [copyFromUserId, setCopyFromUserId] = useState<string>('');
  const [copying, setCopying] = useState(false);

  const isAdminUser = user?.role === 'ADMIN';

  useEffect(() => {
    if (!id || Number.isNaN(userId)) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get<PermissionsResponse | PermissionRow[]>(`/users/${userId}/permissions`);
        const normalized = normalizePermissionsPayload(res.data);
        setPermissions(normalized.permissions);
        setHasCustomRestrictions(normalized.hasCustomRestrictions);
      } catch (e) {
        console.error(e);
        showError('Failed to load permissions');
        setPermissions([]);
        setHasCustomRestrictions(false);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, id, showError]);

  useEffect(() => {
    if (userLoading || !userId) return;
    const loadUsers = async () => {
      try {
        const res = await api.get<User[] | { data: User[] }>('/users');
        const list = Array.isArray(res.data) ? res.data : (res.data as { data: User[] })?.data ?? [];
        setOtherUsers(list.filter((u) => u.id !== userId));
      } catch {
        setOtherUsers([]);
      }
    };
    loadUsers();
  }, [userId, userLoading]);

  const handleCopyFromUser = async () => {
    const sourceId = copyFromUserId ? parseInt(copyFromUserId, 10) : NaN;
    if (Number.isNaN(sourceId)) {
      showError('Select a user to copy from');
      return;
    }
    try {
      setCopying(true);
      const res = await api.get<PermissionsResponse | PermissionRow[]>(`/users/${sourceId}/permissions`);
      const normalized = normalizePermissionsPayload(res.data);
      setPermissions(normalized.permissions);
      setCopyFromUserId('');
      showSuccess('Permissions copied. Review and save to apply.');
    } catch (e) {
      console.error(e);
      showError('Failed to load permissions from user');
    } finally {
      setCopying(false);
    }
  };

  const handleChange = (moduleKey: string, field: 'canView' | 'canEdit' | 'canDelete', value: boolean) => {
    setPermissions((prev) =>
      prev.map((p) => {
        if (p.moduleKey !== moduleKey) return p;
        if (field === 'canView' && !value) {
          return { ...p, canView: false, canEdit: false, canDelete: false };
        }
        return { ...p, [field]: value };
      })
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/users/${userId}/permissions`, { permissions });
      showSuccess('Permissions saved');
      navigate(-1);
    } catch (e) {
      console.error(e);
      showError('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAdminFullAccess = async () => {
    try {
      setSaving(true);
      await api.put(`/users/${userId}/permissions`, { resetToFullAccess: true });
      showSuccess('Admin restored to full unrestricted access');
      navigate(-1);
    } catch (e) {
      console.error(e);
      showError('Failed to reset admin access');
    } finally {
      setSaving(false);
    }
  };

  const permissionsByKey = useMemo(() => {
    const map = new Map<string, PermissionRow>();
    permissions.forEach((p) => map.set(p.moduleKey, p));
    return map;
  }, [permissions]);

  if (userLoading || !user) {
    return (
      <div className="p-6">
        <p>Loading...</p>
      </div>
    );
  }

  const label = (key: string) => MODULE_LABELS[key] || key.replace(/_/g, ' ');

  return (
    <div className="p-6 max-w-4xl flex flex-col h-[calc(100vh-6rem)] min-h-0">
      <div className="flex items-center gap-4 mb-4 flex-shrink-0">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10"
          aria-label="Back"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold">Permissions: {user.userDisplayName || user.userEmail}</h1>
          <p className="text-sm opacity-80">
            {isAdminUser
              ? hasCustomRestrictions
                ? 'Admin with custom screen access — uncheck modules to hide them from the menu.'
                : 'Admin currently has full unrestricted access. Saving will apply custom screen restrictions.'
              : 'View, Edit, and Delete per module'}
          </p>
        </div>
        <div className={cn('flex flex-wrap items-center gap-2 flex-shrink-0', colors.textMuted)}>
          <span className="text-sm">Copy from:</span>
          <select
            value={copyFromUserId}
            onChange={(e) => setCopyFromUserId(e.target.value)}
            disabled={copying || otherUsers.length === 0}
            className={cn(
              'rounded border px-2 py-1.5 text-sm min-w-[180px]',
              colors.border,
              'bg-white dark:bg-gray-800',
              colors.text
            )}
            aria-label="Select user to copy permissions from"
          >
            <option value="">Select user…</option>
            {otherUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.userDisplayName || `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() || u.userEmail}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleCopyFromUser}
            disabled={!copyFromUserId || copying}
          >
            {copying ? 'Copying...' : 'Copy'}
          </Button>
        </div>
      </div>

      {isAdminUser && (
        <div className={cn('mb-4 rounded-lg border px-4 py-3 text-sm flex-shrink-0', colors.border, colors.surface)}>
          {hasCustomRestrictions ? (
            <p>
              This admin has a custom menu. Use <strong>Restore full access</strong> to show all screens again
              (including future modules).
            </p>
          ) : (
            <p>
              By default admins see everything. Uncheck screens they do not need, then save — the sidebar will only
              show what is enabled.
            </p>
          )}
        </div>
      )}

      {loading ? (
        <p>Loading permissions...</p>
      ) : (
        <>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1 -mr-1">
            <Accordion className="space-y-2 pb-4">
              {PERMISSION_GROUPS.map((group) => {
                const rows = group.keys
                  .map((key) => permissionsByKey.get(key))
                  .filter((p): p is PermissionRow => p != null);
                if (rows.length === 0) return null;
                return (
                  <AccordionItem key={group.title} title={group.title} defaultOpen={false}>
                    <div className={`overflow-x-auto rounded border ${colors.border}`}>
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className={colors.surfaceHover}>
                            <th className="p-2 font-medium">Module</th>
                            <th className="p-2 font-medium w-20 text-center">View</th>
                            <th className="p-2 font-medium w-20 text-center">Edit</th>
                            <th className="p-2 font-medium w-20 text-center">Delete</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((p) => (
                            <tr key={p.moduleKey} className={`border-b ${colors.borderLight}`}>
                              <td className="p-2">{label(p.moduleKey)}</td>
                              <td className="p-2 text-center">
                                <div className="flex justify-center">
                                  <Switch
                                    checked={p.canView}
                                    onChange={(e) => handleChange(p.moduleKey, 'canView', e.target.checked)}
                                    size="sm"
                                  />
                                </div>
                              </td>
                              <td className="p-2 text-center">
                                <div className="flex justify-center">
                                  <Switch
                                    checked={p.canEdit}
                                    disabled={!p.canView}
                                    onChange={(e) => handleChange(p.moduleKey, 'canEdit', e.target.checked)}
                                    size="sm"
                                  />
                                </div>
                              </td>
                              <td className="p-2 text-center">
                                <div className="flex justify-center">
                                  <Switch
                                    checked={p.canDelete}
                                    disabled={!p.canView}
                                    onChange={(e) => handleChange(p.moduleKey, 'canDelete', e.target.checked)}
                                    size="sm"
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
          <div className={`mt-4 flex flex-wrap gap-2 flex-shrink-0 pt-2 border-t ${colors.border}`}>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save permissions'}
            </Button>
            {isAdminUser && hasCustomRestrictions && (
              <Button variant="secondary" onClick={handleResetAdminFullAccess} disabled={saving}>
                Restore full access
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default UserPermissionsPage;
